import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMiscStore } from './misc'
import { API } from '../api'

export const useStatuteStore = defineStore('statute', {
	state: () => ({
		statutes: [],
		statute: {},
		sections: [],
		section: {},
		statuteSections: {},
		loadingQueue: {},
		_loading: 0,
		error: null,
	}),

	actions: {
		async _setLoading(status, global = true) {
			if (status) {
				this.error = null
				this._loading = Math.max(this._loading + 1, 0)
			} else {
				this._loading = Math.max(this._loading - 1, 0)
			}

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setLoading(status)
			}
		},

		async _setError(error = null, global = true) {
			this.error = error
			console.group(`StatuteStore`)
			console.warn(error)
			console.groupEnd()

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setError(error)
			}
		},

		async getStatutes(basic = false) {
			if (this.loadingQueue['getStatutes']) return
			this._setLoading(true)

			this.loadingQueue['getStatutes'] = true
			try {
				const statutes = await API.get(`/statutes`, { limit: 0, basic,  })
				this.statutes = statutes
			} catch (error) {
				this._setError(error)
			} finally {
				this.loadingQueue['getStatutes'] = false
				this._setLoading(false)
			}
		},

		async getStatute(statuteId, nocache = false) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const statute = await API.get(`/statutes/${statuteId}`, null, {}, true, {}, false, nocache)
					this.statute = statute
					this.sections = statute.sections
					resolve(statute)
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getStatuteSection(statuteId, sectionId, nocache = false) {
			this._setLoading(true)

			try {
				this.section = await API.get(`/statutes/${statuteId}/sections/${sectionId}`, null, {}, true, {}, false, nocache)
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

			return this.section
		},

		unsetStatute() {
			this.statute = {}
		},

		async getSections(statuteId, nocache = false) {
			this._setLoading(true)

			try {
				const sections = await API.get(`/statutes/${statuteId}/sections`, {limit: 0, }, {}, true, {}, false, nocache)
				 this.sections = sections
				 this.statuteSections[statuteId] = sections?.sort((a, b) => {
					 const aTitle = {...a}.title.replace(/\(|\)/g, '')
					 const bTitle = {...b}.title.replace(/\(|\)/g, '')
					 const na = parseInt(aTitle.match(/\d+/)[0], 10)
					 const nb = parseInt(bTitle.match(/\d+/)[0], 10)
					 if (na !== nb) {
						 return na - nb
					 }
					 return aTitle.localeCompare(bTitle)
				 })
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

			return this.sections
		},

		async getSectionsById(statuteId, [...sectionId]) {
			this._setLoading(true)

			try {
				const { sections } = await API.get(`/statutes/${statuteId}/sections`)
				this.sections = sections
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

		},

		async getSection(statuteId, sectionId) {
			this._setLoading(true)

			let section
			try {
				section = await API.get(`/statutes/${statuteId}/sections/${sectionId}`)
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

			return section
		},

		unsetSections() {
			this.sections = []
		},
	},

	getters: {
		isLoading: state => state._loading > 0,
		statutesSorted: state => state.statutes?.sort((a, b) => Number(a.statuteId) > Number(b.statuteId) ? 1 : -1),
		sectionsSorted: state => state.sections?.sort((a, b) => {
			const aTitle = {...a}.title.replace(/\(|\)/g, '')
			const bTitle = {...b}.title.replace(/\(|\)/g, '')
			const na = parseInt(aTitle.match(/\d+/)[0], 10)
			const nb = parseInt(bTitle.match(/\d+/)[0], 10)
			if (na !== nb) {
				return na - nb
			}
			return aTitle.localeCompare(bTitle)
		 })
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useStatuteStore, import.meta.hot))
}
